/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import { EDatasetExecutionStatus, MERGED_DATASET_TREE_TYPE, SINGLE_DATASET_TREE_TYPE } from "../../../containers/pages/CoverageRequirements/ItemDetails/constants";
import { RedlineDataset } from "../../../containers/pages/CoverageRequirements/ItemDetails/Tabs/ExecutionStatusTab/constants";
import { RootState } from '../../store';
import { COVERAGE_REQUIREMENTS_SLICE_NAME } from "./types";

interface IDialogState {
  isAddStudyDialogOpen: boolean
  isEditStudyDialogOpen: boolean
  isMergeDatasetsDialogOpen: boolean
  isDeleteStudyDialogOpen: boolean
  isDownloadAsCSVDialogOpen: boolean
}

interface IRedlineData {
  data: any
  isLoading: boolean
  errors: string
  recentPollTimestamp: number
}

interface IBenchmarkData {
  isPollingEnabled: boolean
  isLogModeEnabled: boolean
  refreshData: boolean
  concurrentExecutions?: string
  numberOfRuns?: string
  model?: string,
  frontEndStartTimestamp: number
}

export type RedlineDatasetIdToData = {
  [id: string]: RedlineDataset;
};

interface IState {
  dialogState: IDialogState
  coverageRequriementsData: any
  isCoverageRequirementsSaved: boolean
  redlineData: IRedlineData
  cancellingDatasets: any
  datasetsExecutionStatus: any // datasetId -> executionStatus; includes single and merged tree dataset ids.
  datasetsLocalState: any
  mergeSetsLocalState: any
  triggerFetchDatasetData: boolean
  doeMinMaxViolations: any[]
  benchMarkData: IBenchmarkData
  rawRedlineTreeDbItem: any
  mergedRedlineTreeDbItem: any
  redlineDatasetIdsToData: RedlineDatasetIdToData | null,
  clipboard: any

  landingPageDatasetIdToData: any
  studyIdToTreeTypeToTree: any
}

const initialState: IState = {
  dialogState: {
    isAddStudyDialogOpen: false,
    isEditStudyDialogOpen: false,
    isMergeDatasetsDialogOpen: false,
    isDeleteStudyDialogOpen: false,
    isDownloadAsCSVDialogOpen: false
  },
  coverageRequriementsData: null,
  isCoverageRequirementsSaved: true,
  redlineData: {
    data: null,
    isLoading: false,
    errors: '',
    recentPollTimestamp: 0
  },
  cancellingDatasets: null,
  datasetsExecutionStatus: {},
  datasetsLocalState: null,
  mergeSetsLocalState: null,
  triggerFetchDatasetData: false,
  doeMinMaxViolations: {},
  benchMarkData: {
    isPollingEnabled: true,
    isLogModeEnabled: false,
    refreshData: false,
    frontEndStartTimestamp: 0,
    concurrentExecutions: "0",
    numberOfRuns: "0"
  },
  rawRedlineTreeDbItem: null,
  mergedRedlineTreeDbItem: null,
  redlineDatasetIdsToData: null,
  clipboard: null,

  landingPageDatasetIdToData: null,
  studyIdToTreeTypeToTree: null
} as IState


/**
 * Trees
 */
export const enum CoverageRequirementsRedlineTreeDbItemUpdateActionType {
  UpdateAll = 'updateAll',

  FullTreeDbItem = 'fullTreeDbItem',
  UpdateTreeWithoutExecutionStatus = 'updateTreeWithoutExecutionStatus',
  ExecutionStatusUpdate = 'executionStatusUpdate',

  MergedTreeExecutionStatusUpdate = 'mergedTreeExecutionStatusUpdate',
  UpdateMergedTreeWithoutExecutionStatus = 'updateMergedTreeWithoutExecutionStatus',
  UpdateNode = 'updateNode'
}

const replaceNodesInTree = (tree: any, nodeDataMap: any) => {
  if (tree == null) return null;
  // Replace the node with the value from the map if it exists
  const newNodeData = nodeDataMap[tree.nodeId];
  if (newNodeData) {
    tree = {...tree, ...newNodeData};
  }
  // Recursively replace nodes for all children if they exist
  if (tree.children) {
    tree.children = tree.children.map((child: any) => replaceNodesInTree(child, nodeDataMap));
  }
  return tree;
};

const replaceNodesInTreeUsingDatasetId = (tree: any, datasetIdToData: any) => {
  if (tree == null) return null;
  // Replace the node with the value from the map if it exists
  if (tree.datasetId) {
    const newNodeData = datasetIdToData[tree.datasetId];
    if (newNodeData) {
      tree = {...tree, ...newNodeData};
    }
  }
  // Recursively replace nodes for all children if they exist
  if (tree.children) {
    tree.children = tree.children.map((child: any) => replaceNodesInTreeUsingDatasetId(child, datasetIdToData));
  }
  return tree;

}

const triggerFetchDatasetsData = (state: IState) => {
  state.triggerFetchDatasetData = !state.triggerFetchDatasetData
}

const createExecutionStatusMap = (tree: any, map: any): void => {
  if (tree === null) return;

  if (tree.datasetId) {
    map[tree.nodeId] = tree.executionStatus;
  }

  if (tree.children) {
    for (let i = 0; i < tree.children.length; i++) {
      createExecutionStatusMap(tree.children[i], map);
    }
  }
};

const updateExecutionStatus = (tree: any, map: any): void => {
  if (tree === null) return;

  if (tree.datasetId) {
    if (map[tree.nodeId] !== undefined) {
      tree.executionStatus = map[tree.nodeId];
    }
  }

  if (tree.children) {
    for (let i = 0; i < tree.children.length; i++) {
      updateExecutionStatus(tree.children[i], map);
    }
  }
};

const helperUpdateDatasetsExecutionStatus = (state: IState, statusMap: any) => {
  state.datasetsExecutionStatus = {...state.datasetsExecutionStatus, ...statusMap}
}

export const enum EEditDatasetLocalStateActionTypes {
  SINGLE = 'single',
  MULTIPLE = 'multiple'
}

const coverageRequirements = createSlice({
  name: COVERAGE_REQUIREMENTS_SLICE_NAME,
  initialState,
  reducers: {
    clearCoverageRequirementsState: () => initialState,

    /**
     * Landing page
     */
    setLandingPageDatasetIdToData: (state, action: PayloadAction<any>) => {
      state.landingPageDatasetIdToData = {...state.landingPageDatasetIdToData, ...action.payload}
      const {actionType} = action.payload
      switch (actionType) {
        case 'replaceAll': {
          state.landingPageDatasetIdToData = action.payload.data
          break
        }
        case 'updateAll': {
          const {data} = action.payload
          state.landingPageDatasetIdToData = {...state.landingPageDatasetIdToData, ...data}
          break
        }
        case 'deleteDataset': {
          const {datasetId} = action.payload
          if (state.landingPageDatasetIdToData && datasetId in state.landingPageDatasetIdToData) {
            delete state.landingPageDatasetIdToData[datasetId]
          }
          break
        }
        case 'updateDataset': {
          const {data} = action.payload
          const newDatasetId = data.id
          if (state.landingPageDatasetIdToData && newDatasetId in state.landingPageDatasetIdToData) {
            state.landingPageDatasetIdToData[newDatasetId] = {...state.landingPageDatasetIdToData[newDatasetId], ...data}
          }
          break
        }
        case 'addDataset': {
          const {data} = action.payload
          const newDatasetId = data.id
          if (state.landingPageDatasetIdToData) {
            state.landingPageDatasetIdToData[newDatasetId] = data
          } else {
            state.landingPageDatasetIdToData = {[newDatasetId]: data}
          }
          break
        }
        default:
          break
      }
    },

    /**
     * Dashboard
     */
    setRedlineDatasetIdsToData: (state, action: PayloadAction<any>) => {
      const {actionType} = action.payload
      switch (actionType) {
        case 'replaceAll': {
          state.redlineDatasetIdsToData = action.payload.data
          break
        }
        case 'updateAll': {
          const {data} = action.payload
          state.redlineDatasetIdsToData = {...state.redlineDatasetIdsToData, ...data}
          break
        }
        case 'deleteDataset': {
          const {datasetId} = action.payload
          if (state.redlineDatasetIdsToData && datasetId in state.redlineDatasetIdsToData) {
            delete state.redlineDatasetIdsToData[datasetId]
          }
          break
        }
        case 'updateDataset': {
          const {data} = action.payload
          const newDatasetId = data.id
          if (state.redlineDatasetIdsToData && newDatasetId in state.redlineDatasetIdsToData) {
            state.redlineDatasetIdsToData[newDatasetId] = {...state.redlineDatasetIdsToData[newDatasetId], ...data}
          }
          break
        }
        case 'addDataset': {
          const {data} = action.payload
          const newDatasetId = data.id
          if (state.redlineDatasetIdsToData) {
            state.redlineDatasetIdsToData[newDatasetId] = data
          } else {
            state.redlineDatasetIdsToData = {[newDatasetId]: data}
          }
          break
        }
        default:
          break
      }
    },

    /**
     * Trees
     */
    setRawRedlineTreeDbItem: (state, action: PayloadAction<any>) => {
      const {actionType} = action.payload
      switch (actionType) {
        case CoverageRequirementsRedlineTreeDbItemUpdateActionType.FullTreeDbItem:
          const {treeDbItem} = action.payload
          state.rawRedlineTreeDbItem = treeDbItem
          break
        case CoverageRequirementsRedlineTreeDbItemUpdateActionType.UpdateTreeWithoutExecutionStatus:
          const {data} = action.payload
          const {fullTreeDbItem} = data
          const oldTree = state.rawRedlineTreeDbItem.tree

          const executionStatusesToUpdateMap = data.executionStatusesToUpdateMap
          if (executionStatusesToUpdateMap) {
            helperUpdateDatasetsExecutionStatus(state, executionStatusesToUpdateMap)
          }

          // Write function that updates the tree to be equal to fullTreeDbItem, but without updating the execution status
          const updatedTreeDbItemWithoutExecutionStatus = cloneDeep(fullTreeDbItem)
          const updatedTreeWithoutExecutionStatus = updatedTreeDbItemWithoutExecutionStatus.tree

          // TODO: Could use map in redux state instead? just doesn't have node ids, but thats ok cause can use dataset ids instead.
          // Get the execution status of each node in the old tree and use in the new tree
          let executionStatusMap: any = {};
          createExecutionStatusMap(oldTree, executionStatusMap);
          updateExecutionStatus(updatedTreeWithoutExecutionStatus, executionStatusMap);

          state.rawRedlineTreeDbItem = updatedTreeDbItemWithoutExecutionStatus
          break
        case CoverageRequirementsRedlineTreeDbItemUpdateActionType.ExecutionStatusUpdate:
          const {mapDatasetIdToData} = action.payload
          const updatedTreeDbItem = cloneDeep(state.rawRedlineTreeDbItem)
          if (updatedTreeDbItem == null) return
          const updatedTree = updatedTreeDbItem.tree
          replaceNodesInTreeUsingDatasetId(updatedTree, mapDatasetIdToData)
          state.rawRedlineTreeDbItem = updatedTreeDbItem

          // const selectedDatasetId = getCoverageRequirementsSelectedDatasetId()
          const refreshExecutionStatuses = new Set([EDatasetExecutionStatus.COMPLETED, EDatasetExecutionStatus.ABORTED])
          if (action?.payload?.selectedDatasetId) {
            for (const datasetId in mapDatasetIdToData) {
              const executionStatus = mapDatasetIdToData[datasetId].executionStatus
              if (datasetId === action.payload.selectedDatasetId && refreshExecutionStatuses.has(executionStatus)) {
                triggerFetchDatasetsData(state)
              }
            }
          }

          break
        default:
          break
      }
    },
    setMergedRedlineTreeDbItem: (state, action: PayloadAction<any>) => {
      const {actionType} = action.payload
      switch (actionType) {
        case CoverageRequirementsRedlineTreeDbItemUpdateActionType.FullTreeDbItem:
          const {treeDbItem} = action.payload
          state.mergedRedlineTreeDbItem = treeDbItem
          break
        case CoverageRequirementsRedlineTreeDbItemUpdateActionType.UpdateMergedTreeWithoutExecutionStatus:
          /**
           * Updates the tree to be equal to fullTreeDbItem, but without updating the execution status
           */
          const {data} = action.payload
          const {fullTreeDbItem} = data
          const oldTree = state.mergedRedlineTreeDbItem.tree
          if (oldTree == null) {
            state.mergedRedlineTreeDbItem = fullTreeDbItem
            return
          }

          const updatedTreeDbItemWithoutExecutionStatus = cloneDeep(fullTreeDbItem)
          const updatedTreeWithoutExecutionStatus = updatedTreeDbItemWithoutExecutionStatus.tree

          // TODO: Could use map in redux state instead? just doesn't have node ids, but thats ok cause can use dataset ids instead.
          // Get the execution status of each node in the old tree and use in the new tree
          let executionStatusMap: any = {};
          createExecutionStatusMap(oldTree, executionStatusMap);
          if (data.requestType === 'update' && data.nodeIdsToOverwriteExecutionStatus) {
            const {nodeIdsToOverwriteExecutionStatus} = data
            // Remove nodes whose execution status should be from the new tree
            for (const nodeId of nodeIdsToOverwriteExecutionStatus) {
              if (nodeId in executionStatusMap) {
                delete executionStatusMap[nodeId]
              }
            }
          }
          updateExecutionStatus(updatedTreeWithoutExecutionStatus, executionStatusMap);
          if (data.datasetIdsToMarkAsCompleted?.length > 0) {
            const mapDatasetIdToData: any = {}
            for (const datasetId of data.datasetIdsToMarkAsCompleted) {
              mapDatasetIdToData[datasetId] = EDatasetExecutionStatus.COMPLETED
            }
            helperUpdateDatasetsExecutionStatus(state, mapDatasetIdToData)
          }

          state.mergedRedlineTreeDbItem = updatedTreeDbItemWithoutExecutionStatus
          break
        case CoverageRequirementsRedlineTreeDbItemUpdateActionType.MergedTreeExecutionStatusUpdate:
          const {mapDatasetIdToData} = action.payload
          const updatedTreeDbItem = cloneDeep(state.mergedRedlineTreeDbItem)
          if (updatedTreeDbItem == null) return
          const updatedTree = updatedTreeDbItem.tree

          replaceNodesInTreeUsingDatasetId(updatedTree, mapDatasetIdToData)
          state.mergedRedlineTreeDbItem = updatedTreeDbItem
          // const refreshExecutionStatuses = new Set([DATASETS_EXECUTION_STATUS.COMPLETED, DATASETS_EXECUTION_STATUS.ABORTED])
          // if (action?.payload?.selectedDatasetId) {
          //   for (const datasetId in mapDatasetIdToData) {
          //     const executionStatus = mapDatasetIdToData[datasetId].executionStatus
          //     if (datasetId === action.payload.selectedDatasetId && refreshExecutionStatuses.has(executionStatus)) {
          //       triggerFetchDatasetsData(state)
          //     }
          //   }
          // }
          break
        case CoverageRequirementsRedlineTreeDbItemUpdateActionType.UpdateNode:
          const {nodeIdToData} = action.payload

          const updatedMergedTreeDbItem = cloneDeep(state.mergedRedlineTreeDbItem)
          const updatedMergedTree = updatedMergedTreeDbItem.tree
          replaceNodesInTree(updatedMergedTree, nodeIdToData)
          state.mergedRedlineTreeDbItem = updatedMergedTreeDbItem
          break
        default:
          break
      }
    },
    setStudyIdToTreeTypeToTree: (state, action: PayloadAction<any>) => {
      const {actionType} = action.payload
      switch (actionType) {
        case CoverageRequirementsRedlineTreeDbItemUpdateActionType.UpdateAll: {
          const {data} = action.payload
          state.studyIdToTreeTypeToTree = data
          break
        }
        case CoverageRequirementsRedlineTreeDbItemUpdateActionType.FullTreeDbItem: {
          const {treeDbItem, studyId, treeType} = action.payload
          if (!state.studyIdToTreeTypeToTree) {
            state.studyIdToTreeTypeToTree = {}
          }
          state.studyIdToTreeTypeToTree[studyId] = state.studyIdToTreeTypeToTree[studyId] || {}
          state.studyIdToTreeTypeToTree[studyId][treeType] = treeDbItem
          break
        }
        case CoverageRequirementsRedlineTreeDbItemUpdateActionType.UpdateTreeWithoutExecutionStatus: {
          const {data} = action.payload
          const {fullTreeDbItem, studyId, treeType} = data
          if (!state.studyIdToTreeTypeToTree) {
            state.studyIdToTreeTypeToTree = {}
          }
          state.studyIdToTreeTypeToTree[studyId] = state.studyIdToTreeTypeToTree[studyId] || {}
          const oldTree = state.studyIdToTreeTypeToTree[studyId][treeType].tree

          const executionStatusesToUpdateMap = data.executionStatusesToUpdateMap
          if (executionStatusesToUpdateMap) {
            helperUpdateDatasetsExecutionStatus(state, executionStatusesToUpdateMap)
          }

          // Write function that updates the tree to be equal to fullTreeDbItem, but without updating the execution status
          const updatedTreeDbItemWithoutExecutionStatus = cloneDeep(fullTreeDbItem)
          const updatedTreeWithoutExecutionStatus = updatedTreeDbItemWithoutExecutionStatus.tree

          // TODO: Could use map in redux state instead? just doesn't have node ids, but thats ok cause can use dataset ids instead.
          // Get the execution status of each node in the old tree and use in the new tree
          let executionStatusMap: any = {};
          createExecutionStatusMap(oldTree, executionStatusMap);
          if (treeType === MERGED_DATASET_TREE_TYPE && data.requestType === 'update' && data.nodeIdsToOverwriteExecutionStatus) {
            const {nodeIdsToOverwriteExecutionStatus} = data
            // Remove nodes whose execution status should be from the new tree
            for (const nodeId of nodeIdsToOverwriteExecutionStatus) {
              if (nodeId in executionStatusMap) {
                delete executionStatusMap[nodeId]
              }
            }
          }
          updateExecutionStatus(updatedTreeWithoutExecutionStatus, executionStatusMap);
          if (data.datasetIdsToMarkAsCompleted?.length > 0) {
            const mapDatasetIdToData: any = {}
            for (const datasetId of data.datasetIdsToMarkAsCompleted) {
              mapDatasetIdToData[datasetId] = EDatasetExecutionStatus.COMPLETED
            }
            helperUpdateDatasetsExecutionStatus(state, mapDatasetIdToData)
          }

          state.studyIdToTreeTypeToTree[studyId][treeType] = updatedTreeDbItemWithoutExecutionStatus
          break
        }
        case CoverageRequirementsRedlineTreeDbItemUpdateActionType.ExecutionStatusUpdate: {
          const {mapDatasetIdToData, studyId, treeType} = action.payload

          if (!state.studyIdToTreeTypeToTree) {
            state.studyIdToTreeTypeToTree = {}
          }
          state.studyIdToTreeTypeToTree[studyId] = state.studyIdToTreeTypeToTree[studyId] || {}

          const updatedTreeDbItem = cloneDeep(state.studyIdToTreeTypeToTree[studyId][treeType])
          if (updatedTreeDbItem == null) return
          const updatedTree = updatedTreeDbItem.tree
          replaceNodesInTreeUsingDatasetId(updatedTree, mapDatasetIdToData)
          state.studyIdToTreeTypeToTree[studyId][treeType] = updatedTreeDbItem

          // const selectedDatasetId = getCoverageRequirementsSelectedDatasetId()
          const refreshExecutionStatuses = new Set([EDatasetExecutionStatus.COMPLETED, EDatasetExecutionStatus.ABORTED])
          if (treeType === SINGLE_DATASET_TREE_TYPE && action?.payload?.selectedDatasetId) {
            for (const datasetId in mapDatasetIdToData) {
              const executionStatus = mapDatasetIdToData[datasetId].executionStatus
              if (datasetId === action.payload.selectedDatasetId && refreshExecutionStatuses.has(executionStatus)) {
                triggerFetchDatasetsData(state)
              }
            }
          }

          break
        }
        case CoverageRequirementsRedlineTreeDbItemUpdateActionType.UpdateNode:
          const {nodeIdToData, studyId, treeType} = action.payload

          if (treeType !== MERGED_DATASET_TREE_TYPE) {
            return
          }

          if (!state.studyIdToTreeTypeToTree) {
            state.studyIdToTreeTypeToTree = {}
          }
          state.studyIdToTreeTypeToTree[studyId] = state.studyIdToTreeTypeToTree[studyId] || {}

          const updatedMergedTreeDbItem = cloneDeep(state.studyIdToTreeTypeToTree[studyId][treeType])
          const updatedMergedTree = updatedMergedTreeDbItem.tree
          replaceNodesInTree(updatedMergedTree, nodeIdToData)
          state.studyIdToTreeTypeToTree[studyId][treeType] = updatedMergedTreeDbItem
          break
        default:
          break
      }
    },

    /**
     * Dialogs
     */
    setIsAddStudyDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.dialogState.isAddStudyDialogOpen = action.payload
    },
    setIsEditStudyDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.dialogState.isEditStudyDialogOpen = action.payload
    },
    setIsMergeDatasetsDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.dialogState.isMergeDatasetsDialogOpen = action.payload
    },
    setIsDeleteStudyDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.dialogState.isDeleteStudyDialogOpen = action.payload
    },
    setIsDownloadAsCSVDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.dialogState.isDownloadAsCSVDialogOpen = action.payload
    },

    /**
     * Setting dataset data
     */
    setCoverageRequirementsData: (state, action: PayloadAction<any>) => {
      state.coverageRequriementsData = action.payload
    },
    setCoverageRequirementsPropertiesData: (state, action: PayloadAction<any>) => {
      state.coverageRequriementsData.propertiesData = action.payload
    },
    setCoverageRequirementsExperimentData: (state, action: PayloadAction<any>) => {
      state.coverageRequriementsData.experimentsData = action.payload
    },
    setIsCoverageRequirementsSaved: (state, action: PayloadAction<boolean>) => {
      state.isCoverageRequirementsSaved = action.payload
    },
    setTriggerFetchDatasetsData: (state) => {
      triggerFetchDatasetsData(state)
    },

    /**
     * sagas
     */
    getRedlineDataSuccessAction: (state, action: PayloadAction<any>) => {
      state.redlineData.isLoading = false;
      state.redlineData.data = action.payload;
    },
    getRedlineDataErrorAction: (state, action: PayloadAction<string>) => {
      state.redlineData.isLoading = false;
      state.redlineData.errors = action.payload;
    },

    /**
     * Dataset execution
     */
    setIsCancellingDatasets: (state, action: PayloadAction<any>) => {
      state.cancellingDatasets = action.payload
    },
    setDatasetsExecutionStatus: (state, action: PayloadAction<any>) => {
      state.datasetsExecutionStatus = action.payload
    },
    updateDatasetsExecutionStatus: (state, action: PayloadAction<any>) => {
      helperUpdateDatasetsExecutionStatus(state, action.payload)
    },
    editDatasetInExecutionStatusMap: (state, action: PayloadAction<any>) => {
      const {datasetId, executionStatus} = action.payload
      state.datasetsExecutionStatus[datasetId] = executionStatus
    },
    editDatasetLocalState: (state, action: PayloadAction<any>) => {
      if (!state.datasetsLocalState) {
        state.datasetsLocalState = {}
      }

      const {type} = action.payload
      if (type === EEditDatasetLocalStateActionTypes.SINGLE) {
        const {datasetId, data} = action.payload
        if (!state.datasetsLocalState[datasetId]) {
          state.datasetsLocalState[datasetId] = {}
        }
        if (data == null) {
          state.datasetsLocalState[datasetId] = null
        } else {
          state.datasetsLocalState[datasetId] = {...state.datasetsLocalState[datasetId], ...data};
        }
      } else if (type === EEditDatasetLocalStateActionTypes.MULTIPLE) {
        const {data} = action.payload
        state.datasetsLocalState = {...state.datasetsLocalState, ...data}
      }
    },
    editMergeSetLocalState: (state, action: PayloadAction<any>) => {
      const {mergeSetId, data} = action.payload
      // if not present, create a new object
      if (!state.mergeSetsLocalState) {
        state.mergeSetsLocalState = {}
      }
      if (!state.mergeSetsLocalState[mergeSetId]) {
        state.mergeSetsLocalState[mergeSetId] = {}
      }
      if (data == null) {
        state.mergeSetsLocalState[mergeSetId] = null
      } else {
        state.mergeSetsLocalState[mergeSetId] = {...state.mergeSetsLocalState[mergeSetId], ...data};
      }
    },
    setDoeMinMaxViolation: (state, action: PayloadAction<any>) => {
      state.doeMinMaxViolations = action.payload;
    },

    setBenchmarkData: (state, action: PayloadAction<any>) => {
      // state.benchMarkData = {...state.benchMarkData, [action.payload.key] : action.payload.value}
      let currState = cloneDeep(state.benchMarkData)
      state.benchMarkData = {...currState, ...action.payload}
    },
    setCoverageRequirementsClipboard: (state, action: PayloadAction<any>) => {
      const payload = action.payload
      const copyToStudyData = payload?.copyToStudyData
      const nodeToCopy = payload?.nodeToCopy
      const payloadCopy = cloneDeep(payload)
      if (copyToStudyData) {
        // update the previous source data with current
        if (state.clipboard?.copyToStudyData) {
          state.clipboard = {...state.clipboard, copyToStudyData: {...state.clipboard.copyToStudyData, ...copyToStudyData}}
        } else
          state.clipboard = {...state.clipboard, copyToStudyData: copyToStudyData}
        // remove sourceData from payload
        delete payloadCopy.copyToStudyData
      }
      if (nodeToCopy) {
        if (state.clipboard?.nodeToCopy) {
          state.clipboard = {...state.clipboard, nodeToCopy: {...state.clipboard.nodeToCopy, ...nodeToCopy}}
        } else
          state.clipboard = {...state.clipboard, nodeToCopy: nodeToCopy}
        delete payloadCopy.copyToStudyData
      }
      state.clipboard = {...state.clipboard, ...payloadCopy}
    }
  },
})

const selectDialogState = (state: RootState) => state.coverageRequirements.dialogState
const selectCoverageRequirementsDataState = (state: RootState) => state.coverageRequirements.coverageRequriementsData
const selectIsCoverageRequirementsSaved = (state: RootState): boolean => state.coverageRequirements.isCoverageRequirementsSaved
const selectIsCancellingDatasets = (state: RootState) => state.coverageRequirements.cancellingDatasets
// get redline data
const selectRedlineDataState = (state: RootState): IRedlineData => state.coverageRequirements.redlineData
const selectDatasetsExecutionStatus = (state: RootState) => state.coverageRequirements.datasetsExecutionStatus
const selectDatasetsLocalState = (state: RootState) => state.coverageRequirements.datasetsLocalState
const selectMergeSetsLocalState = (state: RootState) => state.coverageRequirements.mergeSetsLocalState
const selectTriggerFetchDatasetData = (state: RootState) => state.coverageRequirements.triggerFetchDatasetData
const selectTriggerDoeMinMaxViolations = (state: RootState) => state.coverageRequirements.doeMinMaxViolations

const selectBenchmarkData = (state: RootState) => state.coverageRequirements.benchMarkData

const selectCoverageRequirementsClipboard = (state: RootState) => state.coverageRequirements.clipboard

/**
 * Landing page
 */
const selectLandingPageDatasetIdToData = (state: RootState) => state.coverageRequirements.landingPageDatasetIdToData

/**
 * Dashboard
 */
const selectRedlineDatasetIdsToData = (state: RootState) => state.coverageRequirements.redlineDatasetIdsToData

/**
 * Trees
 */
const selectRawRedlineTreeDbItem = (state: RootState) => state.coverageRequirements.rawRedlineTreeDbItem
const selectMergedRedlineTreeDbItem = (state: RootState) => state.coverageRequirements.mergedRedlineTreeDbItem
const selectStudyIdToTreeTypeToTree = (state: RootState, treeType: string, studyId: string | undefined) => {
  if (studyId == null) return state.coverageRequirements.studyIdToTreeTypeToTree
  return state.coverageRequirements.studyIdToTreeTypeToTree?.[studyId]?.[treeType]
}

const {actions, reducer} = coverageRequirements

export const {
  clearCoverageRequirementsState,

  /**
   * Landing page
   */
  setLandingPageDatasetIdToData,

  /**
   * Dashboard
   */
  setRedlineDatasetIdsToData,

  /**
   * Trees
   */
  setRawRedlineTreeDbItem,
  setMergedRedlineTreeDbItem,
  setStudyIdToTreeTypeToTree,

  setIsAddStudyDialogOpen,
  setIsEditStudyDialogOpen,
  setIsMergeDatasetsDialogOpen,
  setIsDeleteStudyDialogOpen,
  setIsDownloadAsCSVDialogOpen,
  setCoverageRequirementsData,
  setCoverageRequirementsPropertiesData,
  setCoverageRequirementsExperimentData,
  setIsCoverageRequirementsSaved,
  getRedlineDataSuccessAction,
  getRedlineDataErrorAction,

  setIsCancellingDatasets,
  setDatasetsExecutionStatus,
  updateDatasetsExecutionStatus,
  editDatasetInExecutionStatusMap,
  editDatasetLocalState,
  editMergeSetLocalState,

  setTriggerFetchDatasetsData,
  setDoeMinMaxViolation,
  setCoverageRequirementsClipboard,
  setBenchmarkData,
} = actions
export {
    selectBenchmarkData, selectCoverageRequirementsClipboard, selectCoverageRequirementsDataState, selectDatasetsExecutionStatus,
    selectDatasetsLocalState, selectDialogState, selectIsCancellingDatasets, selectIsCoverageRequirementsSaved,
    /**
     * Landing page
     */
    selectLandingPageDatasetIdToData, selectMergedRedlineTreeDbItem, selectMergeSetsLocalState,
    /**
     * Trees
     */
    selectRawRedlineTreeDbItem,
    /**
     * Dashboard
     */
    selectRedlineDatasetIdsToData,
    // get redline data
    selectRedlineDataState, selectStudyIdToTreeTypeToTree, selectTriggerDoeMinMaxViolations, selectTriggerFetchDatasetData
};
export default reducer
